import {validateAddress} from '@/services/addressValidation/AddressValidationService';
import logError from '@/utils/errors/logError';

type AddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

type PlaceResult = {
  name?: string;
  formatted_address?: string;
  address_components?: AddressComponent[];
};

export interface ParsedAddress {
  addressLineShortened: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  zipCode: string;
  state: string;
  region: string;
}

export interface IAddressFormErrors {
  addressLine1: boolean;
  city: boolean;
  state: boolean;
  zipCode: boolean;
}

export const defaultAddressCountry = 'us';
export const allowedAutocompleteTypes = ['address'];

const getComponent = (
  components: AddressComponent[] | undefined,
  type: string,
  useShortName = false,
): string =>
  components?.find(c => c.types.includes(type))?.[
    useShortName ? 'short_name' : 'long_name'
  ] || '';

export const parseGooglePlaceToAddress = (
  place: PlaceResult,
): ParsedAddress => {
  const components = place?.address_components;
  if (!components) {
    return {
      addressLineShortened: '',
      addressLine1: '',
      city: '',
      zipCode: '',
      state: '',
      region: '',
    };
  }

  const city =
    getComponent(components, 'locality') ||
    getComponent(components, 'sublocality_level_1', true) ||
    getComponent(components, 'administrative_area_level_2', true);

  const streetNumber = getComponent(components, 'street_number', true);
  const route = getComponent(components, 'route', true);
  const addressLineShortened = streetNumber
    ? `${streetNumber} ${route}`
    : route || '';

  const addressLine2 = getComponent(components, 'subpremise');

  return {
    addressLineShortened: addressLineShortened || place?.name || '',
    addressLine1: place?.formatted_address || '',
    addressLine2: addressLine2 || '',
    city,
    zipCode: getComponent(components, 'postal_code'),
    state: getComponent(components, 'administrative_area_level_1'),
    region: getComponent(components, 'administrative_area_level_1', true),
  };
};

export const validateAddressForm = (
  formAddress: Partial<ParsedAddress>,
  hasSelectedAddressBefore: boolean,
): IAddressFormErrors => {
  const {addressLineShortened, addressLine1, city, state, zipCode} =
    formAddress;

  if (!hasSelectedAddressBefore || !addressLine1)
    return {
      addressLine1: false,
      city: false,
      state: false,
      zipCode: false,
    };

  const isAddressLineInvalid =
    !addressLineShortened || !addressLine1 || !city || !state || !zipCode;

  return {
    addressLine1: isAddressLineInvalid,
    city: !city,
    state: !state,
    zipCode: !zipCode,
  };
};

export const getAddressFromInputValue = (
  inputValue: string,
  lastSelectedAddress?: ParsedAddress,
): Partial<ParsedAddress> => {
  if (!lastSelectedAddress || !inputValue) {
    return {
      addressLine1: '',
      addressLineShortened: '',
      city: '',
      zipCode: '',
      state: '',
      region: '',
    };
  }

  const lowerValue = inputValue.toLowerCase();

  return {
    addressLineShortened: lowerValue.includes(
      lastSelectedAddress.addressLineShortened.toLowerCase(),
    )
      ? lastSelectedAddress.addressLineShortened
      : '',
    city: lowerValue.includes(lastSelectedAddress.city.toLowerCase())
      ? lastSelectedAddress.city
      : '',
    state: lowerValue.includes(lastSelectedAddress.region.toLowerCase())
      ? lastSelectedAddress.state
      : '',
    region: lowerValue.includes(lastSelectedAddress.region.toLowerCase())
      ? lastSelectedAddress.region
      : '',
    zipCode: inputValue.includes(lastSelectedAddress.zipCode)
      ? lastSelectedAddress.zipCode
      : '',
  };
};

/**
 * Represents a normalized postal address used for address validation.
 *
 * This interface is intended to capture the minimum required address fields
 * needed to perform address validation and PO Box detection via external
 * address validation services (e.g. Google Address Validation API).
 *
 * All fields are expected to be plain, unformatted strings.
 */
interface AddressValidationProp {
  /** Primary street address line (e.g. "123 Main St" or "PO Box 456") */
  line1: string;
  city: string;
  /** State, province, or administrative area (e.g. "CA", "NY") */
  state: string;
  postal_code: string;
  /** Country name or ISO country code (e.g. "US") */
  country: string;
}

/**
 * Determines whether a given address corresponds to a PO Box.
 *
 * This utility function validates the provided address using an external
 * address validation service and inspects the response metadata to identify
 * whether the address is classified as a PO Box.
 *
 * The function is designed to fail safely: if the external validation request
 * fails or returns an unexpected response, the function will log the error
 * and return `false` instead of throwing.
 *
 * @param address - The address object to be validated.
 * @param regionCode - Optional region code used by the address validation API
 * (defaults to `"US"`).
 *
 * @returns A promise that resolves to:
 * - `true` if the address is identified as a PO Box
 * - `false` if the address is not a PO Box or if validation fails
 *
 * @remarks
 * - This function makes an external API call and should be treated as
 *   asynchronous and potentially slow.
 * - Errors are internally logged and are not propagated to the caller.
 * - Intended for business rules where PO Box addresses are restricted or
 *   require special handling.
 */
export const isPoBoxAddress = async (
  address: AddressValidationProp,
  regionCode: string = 'US',
): Promise<boolean> => {
  try {
    const addressLines = `${address.line1}, ${address.city}, ${address.state} ${address.postal_code} ${address.country}`;
    const data = await validateAddress({
      addressLines: [addressLines],
      regionCode,
    });

    return Boolean(data.result?.metadata?.poBox);
  } catch (error) {
    logError(error, {
      customTitle: 'PoBox Address Validation Failed',
      fileName: 'AddressUtils.ts',
      functionName: 'isPoBoxAddress',
    });
    return false;
  }
};
